import { useEffect, useState } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import Login from "./pages/Login";
import VerificaEmail from "./pages/VerificaEmail";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import DPI from "./pages/DPI"
import VineyardDetail from "./pages/VineyardDetail";
import Diagnose from "./pages/Diagnose";
import Fertilizer from "./pages/Fertilizer";
import Meteo from "./pages/Meteo";
import LinkUtili from "./pages/LinkUtili";
import GestioneVite from "./pages/GestioneVite";
import Forum from "./pages/Forum";
import ForumThread from "./pages/ForumThread";
import Journal from "./pages/Journal";
import OtherCrops from "./pages/OtherCrops";
import Catalogo from "./pages/Catalogo";
import About from "./pages/About";
import Profile from "./pages/Profile";
import Suggestions from "./pages/Suggestions";
import Cestino from "./pages/Cestino";
import FloatingHelpChat from "./components/FloatingHelpChat";
import PaywallScreen from "./components/PaywallScreen";
import { AuthProvider, useAuth, setStoredToken } from "./context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

axios.interceptors.response.use(
  (resp) => resp,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    if (status === 401 && !url.includes("/auth/me") && !url.includes("/auth/session") && !url.includes("/suggestions/unread-count")) {
      if (window.location.pathname !== "/") {
        window.location.href = "/";
      }
    }
    return Promise.reject(error);
  }
);

function AuthCallback() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    // Il backend ci rimanda qui dopo Google con il token gia' pronto nel
    // fragment: #session_token=...&new=0|1. Il fragment non viaggia verso
    // nessun server, e lo ripuliamo subito dall'URL.
    const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const sessionToken = params.get("session_token");
    const isNewUser = params.get("new") === "1";
    if (!sessionToken) {
      navigate("/", { replace: true });
      return;
    }
    (async () => {
      try {
        setStoredToken(sessionToken);
        window.history.replaceState(null, "", "/dashboard");
        // checkAuth carica l'utente completo da /auth/me (country incluso, cosi'
        // userCountryLang e' corretto prima del redirect) e valida il token.
        const loggedUser = await checkAuth();
        if (cancelled) return;
        if (!loggedUser) throw new Error("Sessione non valida");

        if (isNewUser && typeof window.gtag === "function") {
          window.gtag("event", "sign_up", { method: "google" });
          window.gtag("event", "conversion", {
            send_to: "AW-18183121570/k-diCL2_jcEcEKLVsd5D",
            value: 1.0,
            currency: "EUR",
            transaction_id: `signup-${Date.now()}`,
          });
        }

        navigate("/dashboard", { replace: true });
      } catch (e) {
        if (cancelled) return;
        setStoredToken(null);
        setError("Autenticazione fallita. Riprova.");
        setTimeout(() => navigate("/", { replace: true }), 2000);
      }
    })();
    return () => { cancelled = true; }
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="auth-callback">
      <div className="text-center">
        <div className="vs-spinner mx-auto mb-3" />
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>
          {error || "Accesso in corso..."}
        </p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading, setUser } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="vs-spinner" />
      </div>
    );
  }

  if (!user) return <Navigate to="/" replace />;

  // Controlla abbonamento
  const sub = user.subscription;
  if (sub && !sub.active) {
    return (
      <PaywallScreen
        user={user}
        onSubscribed={(updatedUser) => setUser(updatedUser)}
      />
    );
  }

  return children;
}

function AppRouter() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, setUser } = useAuth();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("payment") === "success" && user) {
      (async () => {
        try {
          await axios.post(`${API}/stripe/sync-subscription`);
          const res = await axios.get(`${API}/auth/me`);
          setUser(res.data);
          toast.success("🎉 Abbonamento attivato! Benvenuto in Premium.");
        } catch (e) {
          console.error("Errore sync abbonamento:", e);
        } finally {
          navigate("/dashboard", { replace: true });
        }
      })();
    }
  }, [user]);

  if (location.hash?.includes("session_token=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <Routes>
        <Route path="/" element={<Login />} />
        <Route path="/dpi" element={<ProtectedRoute><DPI /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/vigneto/:id" element={<ProtectedRoute><VineyardDetail /></ProtectedRoute>} />
        <Route path="/diagnosi" element={<ProtectedRoute><Diagnose /></ProtectedRoute>} />
        <Route path="/concimi" element={<ProtectedRoute><Fertilizer /></ProtectedRoute>} />
        <Route path="/meteo" element={<ProtectedRoute><Meteo /></ProtectedRoute>} />
        <Route path="/link-utili" element={<ProtectedRoute><LinkUtili /></ProtectedRoute>} />
        <Route path="/gestione-vite" element={<ProtectedRoute><GestioneVite /></ProtectedRoute>} />
        <Route path="/forum" element={<ProtectedRoute><Forum /></ProtectedRoute>} />
        <Route path="/forum/:id" element={<ProtectedRoute><ForumThread /></ProtectedRoute>} />
        <Route path="/quaderno" element={<ProtectedRoute><Journal /></ProtectedRoute>} />
        <Route path="/altre-colture" element={<ProtectedRoute><OtherCrops /></ProtectedRoute>} />
        <Route path="/catalogo" element={<Catalogo />} />
        <Route path="/verifica-email" element={<VerificaEmail />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/info" element={<ProtectedRoute><About /></ProtectedRoute>} />
        <Route path="/profilo" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/suggerimenti" element={<ProtectedRoute><Suggestions /></ProtectedRoute>} />
        <Route path="/cestino" element={<ProtectedRoute><Cestino /></ProtectedRoute>} />  
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <FloatingHelpChat />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
